import React from 'react'
import { SurveyQuestionType } from '../models/Survey'
import type { Division } from '../models/Division'
import { Typography } from '@mui/material'

import { type ISurveyItem } from '../models/SurveyTemplate'
interface percenatgeProps {
  completionCount: number
  studentCount: number
  isAnonymousSurvey: boolean
}
// @TODO All helpers are in same file. we should have separate different files for each other

// return percent from 0 to 100
export function getPercent (part: number, total: number): number {
  if (total === 0) return 0
  const result = (100 * part) / total
  if (result < 1 && result > 0) {
    return 1
  }
  return Math.round(result)
}

// return average score, with each item in the list weighted (index 0 is 1, index 1 is 2, etc).
// Total is the total number of entries
export function getAverageScore (values: number[]): number {
  const total = values?.reduce((acc, val) => acc + val, 0)
  if (total === 0) return 0
  return values?.reduce((acc, cur, i) => acc + (i + 1) * cur, 0) / total
}

export function roundedPercentages (percentages: number[]): number[] {
  const totalPercentage = percentages?.reduce((sum, percentage) => sum + percentage, 0)
  const roundedPercentages = percentages?.map(percentage => Math.floor((percentage / totalPercentage) * 100))

  const remainder = 100 - roundedPercentages?.reduce((sum, percentage) => sum + percentage, 0)
  const sortedIndexes = Array.from({ length: percentages.length }, (_, index) => index).sort((a, b) => percentages[b] - percentages[a])

  for (let i = 0; i < remainder; i++) {
    roundedPercentages[sortedIndexes[i % percentages.length]]++
  }

  return roundedPercentages
}

export function breadcrumbToList (division: Division): Division[] {
  let cur: Division | null = division
  const divisions: Division[] = []

  while (cur != null) {
    divisions.push(cur)
    cur = cur?.children != null && cur.children.length > 0 ? cur?.children[0] : null
  }
  return divisions
}

export function getDivisionFromBreadcrumb (breadcrumb: Division | null): Division | null {
  if (breadcrumb === null) {
    return null
  }

  const divisions = breadcrumbToList(breadcrumb)

  if (divisions?.length === 0) {
    return null
  }

  return divisions[divisions?.length - 1]
}

export const countNonDescriptiveQuestions = (surveyItems: ISurveyItem[]): number => {
  return surveyItems?.filter((question: ISurveyItem) => question.itemType !== SurveyQuestionType.Title)?.length
}

export function titleFormatted (title: string = ''): JSX.Element {
  if (title === '') {
    return (<></>)
  }

  const colonPos = title.indexOf(':')

  if (colonPos > 0) {
    const bolded = title.substring(0, colonPos + 1)
    const after = title.substring(colonPos + 1)
    return (<Typography tabIndex={0} component="h2" variant="h6"><b>{bolded}</b>{after}</Typography>)
  }

  return (<Typography tabIndex={0} component="h2" variant="h5">{title}</Typography>)
}
/**
 * Calculates response percentage for a report attribute.
 *
 * Rules:
 * - Anonymous survey → return studentCount directly.
 * - Non-anonymous survey → (completionCount / studentCount) * 100.
 * - Prevents division by zero.
 * - Normalizes null/undefined values to 0.
 * - Ensures result is never NaN or Infinity.
 * - Does NOT modify floating precision.
 */

export const calculateResponseForReportAttribute = (
  reportResponseValue: percenatgeProps
): number => {
  const {
    isAnonymousSurvey,
    completionCount,
    studentCount
  } = reportResponseValue

  // Normalize null/undefined to 0
  const safeCompletionCount = completionCount ?? 0
  const safeStudentCount = studentCount ?? 0

  // Case 1: Anonymous survey
  if (isAnonymousSurvey) {
    return Number.isFinite(safeStudentCount) ? safeStudentCount : 0
  }

  // Case 2: Non-anonymous survey
  // Prevent division by zero or negative denominator
  if (safeStudentCount <= 0) {
    return 0
  }

  const percent = (safeCompletionCount / safeStudentCount) * 100

  // Extra safety: avoid NaN or Infinity
  return Number.isFinite(percent) ? percent : 0
}
